/**
 * O passo determinístico da run (20 Hz). MESMO código roda em cliente e servidor (A2):
 * proibido Math.random/sin/cos/pow/Date — apenas rng.ts, sintab.ts, xptab.ts e aritmética IEEE.
 * Ordem fixa do tick: fase → spawn → movimento → contato → ataque → gemas → limpeza.
 */
import { createRng, type Rng } from './rng.ts';
import { sinT, cosT } from './sintab.ts';
import { xpForLevel } from './xptab.ts';
import { aliveCap, enemyHp, pickFamily, spawnRate } from './spawn.ts';
import { ENEMY_FAMILIES } from '../data/index.ts';
import { applyWeapons } from './loadout.ts';
import { applyCard, autoPick, generateDraft } from './draft.ts';
import {
  NENHUM_BONUS, bonusCargaEspecial, bonusDanoArmas, bonusHpInicial, bonusVelocidade, rerollsDisponiveis,
} from './meta.ts';
import { atualizarObjetivo, criarObjetivo } from './objectives.ts';
import { NORNO_HP, atualizarNorno, criarNorno } from './norno.ts';
import { encontroPalhaco, fragmentoEstrela } from './story.ts';
import { comboDe, especialDoHeroi } from './combos.ts';
import {
  BADALADA_DURACAO_TICKS, BADALADA_INICIO_TICK, DURACAO_RUN_TICKS, GUARDIAO_TICK,
  TICKS_POR_SEGUNDO, type EnemySim, type GemSim, type HeroInput, type RunState, type SimConfig,
  type StepOpts,
} from './types.ts';
import type { Card } from './draft.ts';

/** Resolve o draft pendente (modo interativo): aplica a carta e descongela a sim. */
export function resolveDraft(state: RunState, card: Card): void {
  if (!state.pendingDraft) return;
  const hero = state.heroes[state.pendingDraft.heroIdx];
  if (hero) applyCard(hero, card);
  state.pendingDraft = null;
}

const DT = 1 / TICKS_POR_SEGUNDO;

// Baseline A.2 (Pip) — heróis data-driven completos chegam com o draft
const PIP = { hp: 170, speed: 190, dano: 12, cadencia: 1.2, raio: 52, knockback: 16 };
const GEM_PICKUP_RAIO = 24;
const CONTACT_CD_TICKS = TICKS_POR_SEGUNDO; // 1s por inimigo
const IFRAME_TICKS = Math.round(TICKS_POR_SEGUNDO / 3); // 0,33s de contato normal; o sufoco (4b) pune o cerco
// Luar é a moeda META (rara): alvo A.6 ≈ 120/run no cap.1 (custos de prédio 100×1,6^k).
// Calibrado por simulação (AC-M3-04, reports/economia.csv): 0,35 dava ~817/run (6,8× o alvo).
const LUAR_CHANCE = 0.051;
// sufocamento: >LIMIAR inimigos dentro de RAIO → dano/s por inimigo excedente (bypassa i-frame)
const SUFOCO_RAIO = 120;      // raio de detecção de cerco
const SUFOCO_SETORES_MIN = 6; // ≥6 de 8 octantes ocupados = cercado
const SUFOCO_DANO = 9;        // dano/s por octante excedente
// Badalada Sombria (AC-M3-06): opt-in → janela de 60s dobrando os elites + baú garantido.
// Elites são feature EXCLUSIVA do opt-in (sem opt-in: zero elites → determinismo/golden/economia intactos).
export const SOMBRIA_DURACAO_TICKS = 60 * TICKS_POR_SEGUNDO; // 60 s
export const ELITE_CHANCE_BASE = 0.15;               // taxa nominal de elite
export const ELITE_MULT_SOMBRIA = 2;                 // a Sombria DOBRA (doc 08 AC-M3-06)
export const ELITE_CHANCE_SOMBRIA = ELITE_CHANCE_BASE * ELITE_MULT_SOMBRIA; // 0,30 durante a janela
export const ELITE_HP_MULT = 4;                      // elite é bem mais tanque
// Golemita (Guardião do Cemitério, doc 03 §6.2) — arena "fecha", derrotá-lo = vitória
const BOSS = { hp: 2000, speed: 42, dano: 22, raioContato: 40, raio: 26 };
const BOSS_GRACE_TICKS = 3 * 60 * TICKS_POR_SEGUNDO; // 3 min extras para vencer o Guardião
// Revive co-op (doc 03 §3.5, A.5): cair a 0 HP → "down"; aliado por perto 3s → volta a 50% HP
const REVIVE_RADIO = 52;
const REVIVE_HOLD = 3 * TICKS_POR_SEGUNDO; // 3 s de "cafuné"
const DOWN_WINDOW = 15 * TICKS_POR_SEGUNDO; // sem revive nesta janela → morre
const REVIVE_HP_FRAC = 0.5;
// Especiais e combos (doc 03 §3.5 + A.3)
const SPECIAL_MAX = 100;
const SPECIAL_RADIO = 180; // Muralha de Cenouras (Pip) — rajada em anel
const SPECIAL_DANO = 80;
const COMBO_WINDOW = 2 * TICKS_POR_SEGUNDO; // 2 especiais em <2s = combo
const COMBO_DANO = 120; // bônus do combo: limpa a tela

export function createRun(config: SimConfig): RunState {
  const rng = createRng(config.seed);
  const meta = config.meta ?? NENHUM_BONUS;
  const hpInicial = PIP.hp * bonusHpInicial(meta.curandeiro); // Curandeiro (no-op no nv0)
  const velocidade = PIP.speed * bonusVelocidade(meta.corredor); // Corredor (no-op no nv0)
  const heroes = Array.from({ length: config.playerCount }, (_, i) => ({
    id: i,
    heroId: config.heroIds?.[i] ?? 'pip',
    especialId: especialDoHeroi(config.heroIds?.[i] ?? 'pip'),
    x: config.arenaW / 2 + i * 40 - (config.playerCount - 1) * 20,
    y: config.arenaH / 2,
    hp: hpInicial,
    maxHp: hpInicial,
    speed: velocidade,
    level: 1,
    xp: 0,
    attackCd: 0,
    iframeCd: 0,
    alive: true,
    down: false,
    downTicks: 0,
    reviveTicks: 0,
    special: 0,
    kills: 0,
    weapons: [{ id: 'bola_de_fogo', level: 1, evolved: false }], // arma inicial do Pip
    passives: [] as string[],
    orbitPhase: 0,
    autoRevives: config.modoCoragem ? 1 : 0, // ACC-6 (Modo Coragem)
  }));
  // AC-M4-04: boss final Norno — presente desde o início na run opt-in `config.norno`
  const enemies: EnemySim[] = [];
  let nextId = 1;
  if (config.norno) {
    const nb: EnemySim = {
      id: nextId++, family: 'norno', x: config.arenaW / 2, y: config.arenaH / 2 - 340, vx: 0, vy: 0,
      hp: NORNO_HP, maxHp: NORNO_HP, phase: 0, contactCd: 0, alive: true, boss: true,
    };
    enemies.push(nb);
  }
  return {
    tick: 0,
    phase: 'aquecimento',
    heroes,
    enemies,
    gems: [],
    stats: { spawned: 0, kills: 0, luar: 0, gemsColetadas: 0, dropsNaBadalada: 0, elites: 0, chests: 0 },
    spawnAcc: 0,
    nextId,
    pendingDraft: null,
    bossDefeated: false,
    rerollsDisponiveis: rerollsDisponiveis(meta.sortudo), // Sortudo (1 grátis no nv0)
    objetivo: config.norno ? null : criarObjetivo(config.chapter, config.arenaW, config.arenaH), // null no cap.1 e no Norno
    norno: config.norno ? criarNorno(config.arenaW, config.arenaH) : null, // AC-M4-04
    palhaco: !config.norno && encontroPalhaco(config.chapter), // AC-M4-02 (encontro nos caps 3/5/7)
    fragmento: !config.norno && fragmentoEstrela(config.chapter), // AC-M4-03 (coleta nos caps 3/6/9)
    lastSpecialTick: config.playerCount > 0 ? Array.from({ length: config.playerCount }, () => -1) : [],
    combos: [],
    rngState: rng.state(),
  };
}

function rngFrom(state: RunState): Rng {
  // retoma o stream exatamente de onde parou (estado serializado)
  const [a, b, c, d] = state.rngState;
  return restore(a, b, c, d);
}

/** wrapper interno: reimplementa o xoshiro sobre estado bruto (mesma matemática do rng.ts) */
function restore(s0: number, s1: number, s2: number, s3: number): Rng {
  const rotl = (x: number, k: number): number => ((x << k) | (x >>> (32 - k))) >>> 0;
  function nextU32(): number {
    const result = Math.imul(rotl(Math.imul(s1, 5) >>> 0, 7), 9) >>> 0;
    const t = (s1 << 9) >>> 0;
    s2 = (s2 ^ s0) >>> 0;
    s3 = (s3 ^ s1) >>> 0;
    s1 = (s1 ^ s2) >>> 0;
    s0 = (s0 ^ s3) >>> 0;
    s2 = (s2 ^ t) >>> 0;
    s3 = rotl(s3, 11);
    return result;
  }
  return {
    nextU32,
    next01: () => ((nextU32() >>> 9) / 0x7fffff / 1.0000001192092896) % 1,
    int: (min: number, max: number) => min + (nextU32() % ((max - min + 1) >>> 0)),
    fork: (id: number) => createRng((s0 ^ nextU32()) >>> 0, id),
    state: () => [s0, s1, s2, s3] as const,
  };
}

export function step(state: RunState, config: SimConfig, inputs: HeroInput[], opts?: StepOpts): RunState {
  if (state.pendingDraft) return state; // congelado até resolveDraft (modo interativo)
  const rng = rngFrom(state);
  const tMin = state.tick / TICKS_POR_SEGUNDO / 60;
  // bônus Sutarifu (AC-M3-05) — todos ×1 / no-op quando não há meta (sim base preservada)
  const meta = config.meta ?? NENHUM_BONUS;
  const danoMul = bonusDanoArmas(meta.ferreiro); // Ferreiro
  const cargaMul = bonusCargaEspecial(meta.magico); // Mágico

  // ── 1. fase ─────────────────────────────────────────────
  // A partir de GUARDIAO_TICK a run entra na fase do Golemita: a VITÓRIA vem de derrotá-lo
  // (seção 5c), não do relógio. Se ele resistir até o tempo esgotar, a noite vence (derrota).
  if (state.phase !== 'derrota' && state.phase !== 'vitoria') {
    if (state.norno) {
      // AC-M4-04: luta contra o Norno (sem Golemita/relógio); vence pelos ponteiros (5d), perde se morrer
      state.phase = 'guardiao';
    } else if (!state.objetivo) {
      // cap.1 (fluxo clássico do Golemita) — INALTERADO
      if (state.tick >= GUARDIAO_TICK) {
        state.phase = 'guardiao';
        if (state.tick >= DURACAO_RUN_TICKS + BOSS_GRACE_TICKS) state.phase = 'derrota'; // não matou a tempo
      } else if (state.tick >= BADALADA_INICIO_TICK && state.tick < BADALADA_INICIO_TICK + BADALADA_DURACAO_TICKS) {
        state.phase = 'badalada';
      } else if (tMin >= 1) {
        state.phase = 'hordas';
      }
    } else {
      // cap.2–4: sem Golemita; vence pelo objetivo (seção 5d); estourar o tempo = derrota
      if (state.tick >= DURACAO_RUN_TICKS + BOSS_GRACE_TICKS) state.phase = 'derrota';
      else if (state.tick >= BADALADA_INICIO_TICK && state.tick < BADALADA_INICIO_TICK + BADALADA_DURACAO_TICKS) {
        state.phase = 'badalada';
      } else if (tMin >= 1) state.phase = 'hordas';
    }
    if (state.heroes.every((h) => !h.alive)) state.phase = 'derrota';
  }
  const emBadalada = state.phase === 'badalada';
  const fim = state.phase === 'vitoria' || state.phase === 'derrota';
  if (fim) {
    state.rngState = rng.state();
    state.tick += 1;
    return state;
  }

  const cx = state.heroes.reduce((s, h) => s + h.x, 0) / state.heroes.length;
  const cy = state.heroes.reduce((s, h) => s + h.y, 0) / state.heroes.length;
  // snapshots p/ carga do especial (abates deste tick por herói + gemas deste tick)
  const kills0 = state.heroes.map((h) => h.kills);
  const gems0 = state.stats.gemsColetadas;

  // ── 1b. Golemita: entra na fase guardião, arena "fecha" (para spawns normais) ──
  const emGuardiao = state.phase === 'guardiao';
  if (emGuardiao && !state.bossDefeated && !state.enemies.some((e) => e.boss && e.alive)) {
    const boss: EnemySim = {
      id: state.nextId++, family: 'golemita', x: cx, y: cy - 320, vx: 0, vy: 0,
      hp: BOSS.hp * (1 + 0.12 * (config.chapter - 1)), maxHp: 0,
      phase: 0, contactCd: 0, alive: true, boss: true,
    };
    boss.maxHp = boss.hp;
    state.enemies.push(boss);
  }

  // ── 2. spawn (suspenso durante a fase do Guardião) ──────
  // Badalada Sombria (AC-M3-06): janela de 60s a partir do início da Badalada, só com opt-in.
  const emSombria =
    config.badaladaSombria === true &&
    config.modoCoragem !== true && // ACC-6: Modo Coragem não tem elites
    state.tick >= BADALADA_INICIO_TICK &&
    state.tick < BADALADA_INICIO_TICK + SOMBRIA_DURACAO_TICKS;
  // baú garantido: uma vez, no primeiro tick da janela
  if (config.badaladaSombria === true && state.tick === BADALADA_INICIO_TICK) state.stats.chests += 1;
  const cap = aliveCap(tMin);
  let podeSpawnar = false;
  if (!emGuardiao) {
    const vivos = state.enemies.reduce((n, e) => (e.alive ? n + 1 : n), 0);
    state.spawnAcc += spawnRate(tMin, config.playerCount, config.modoCoragem) * DT * (emBadalada ? 1.3 : 1);
    podeSpawnar = state.spawnAcc >= 1 && vivos < cap;
  }
  while (podeSpawnar) {
    state.spawnAcc -= 1;
    const fam = pickFamily(rng);
    const ang = rng.next01(); // turns
    const raio = rng.int(560, 640);
    // elite: só na janela da Sombria (rng só é consumido com opt-in → caminho base intacto)
    const elite = emSombria && rng.next01() < ELITE_CHANCE_SOMBRIA;
    const e: EnemySim = {
      id: state.nextId++,
      family: fam.id,
      x: cx + cosT(ang) * raio,
      y: cy + sinT(ang) * raio,
      vx: 0,
      vy: 0,
      hp: enemyHp(tMin, fam.hpFator, config.playerCount, config.chapter) * (elite ? ELITE_HP_MULT : 1),
      maxHp: 0,
      phase: rng.next01(),
      contactCd: 0,
      alive: true,
      ...(elite ? { elite: true } : {}),
    };
    e.maxHp = e.hp;
    if (elite) state.stats.elites += 1;
    if (fam.comportamento === 'rolar') {
      const d = dirTo(e.x, e.y, cx, cy);
      e.vx = d.x * fam.velocidade;
      e.vy = d.y * fam.velocidade;
    }
    state.enemies.push(e);
    state.stats.spawned += 1;
    podeSpawnar = state.spawnAcc >= 1 && state.enemies.reduce((n, x) => (x.alive ? n + 1 : n), 0) < cap;
  }

  // ── 3. movimento de heróis (input clampado — anti-speedhack) ──
  state.heroes.forEach((h, i) => {
    if (!h.alive || h.down) return;
    const inp = inputs[i] ?? { mx: 0, my: 0 };
    const mx = Math.max(-1, Math.min(1, inp.mx));
    const my = Math.max(-1, Math.min(1, inp.my));
    const len = Math.sqrt(mx * mx + my * my);
    const nx = len > 1 ? mx / len : mx;
    const ny = len > 1 ? my / len : my;
    h.x = clamp(h.x + nx * h.speed * DT, 16, config.arenaW - 16);
    h.y = clamp(h.y + ny * h.speed * DT, 16, config.arenaH - 16);
    if (h.attackCd > 0) h.attackCd -= 1;
    if (h.iframeCd > 0) h.iframeCd -= 1;
    h.orbitPhase = (h.orbitPhase + 0.6 * DT) % 1; // armas orbitais giram
  });

  // ── 3b. especiais + combos de ultimate (co-op) ──────────
  state.heroes.forEach((h, i) => {
    if (!h.alive || h.down) return;
    if (!inputs[i]?.special || h.special < SPECIAL_MAX) return;
    // dispara o especial da assinatura (M1: Pip "Muralha de Cenouras" → rajada em anel)
    burstDamage(state, h, h.x, h.y, SPECIAL_RADIO, SPECIAL_DANO);
    h.special = 0;
    // COMBO: outro herói ativou nos últimos COMBO_WINDOW ticks?
    for (let j = 0; j < state.heroes.length; j++) {
      if (j === i) continue;
      const outro = state.lastSpecialTick[j] ?? -1;
      if (outro >= 0 && state.tick - outro <= COMBO_WINDOW) {
        const nome = comboDe(state.heroes[j]!.especialId, h.especialId); // AC-M4-05: combo data-driven
        state.combos.push({ name: nome, tick: state.tick, heroIdxs: [j, i] });
        // bônus do combo: limpa a tela (dano grande em todos)
        for (const e of state.enemies) {
          if (e.alive && !e.boss) e.hp -= COMBO_DANO;
        }
        break;
      }
    }
    state.lastSpecialTick[i] = state.tick;
  });

  // ── 4. movimento de inimigos + contato ──────────────────
  for (const e of state.enemies) {
    if (!e.alive) continue;
    // Golemita: persegue devagar, contato forte (sem i-frame especial — o herói kita)
    if (e.boss) {
      const alvoB = nearestHero(state, e.x, e.y);
      if (alvoB) {
        const d = dirTo(e.x, e.y, alvoB.x, alvoB.y);
        e.x += d.x * BOSS.speed * DT;
        e.y += d.y * BOSS.speed * DT;
        if (e.contactCd > 0) e.contactCd -= 1;
        if (e.contactCd === 0 && alvoB.iframeCd === 0 &&
            dist2(e.x, e.y, alvoB.x, alvoB.y) <= BOSS.raioContato * BOSS.raioContato) {
          alvoB.hp = Math.max(0, alvoB.hp - BOSS.dano);
          e.contactCd = CONTACT_CD_TICKS;
          alvoB.iframeCd = IFRAME_TICKS;
        }
      }
      continue;
    }
    const fam = ENEMY_FAMILIES.find((f) => f.id === e.family);
    if (!fam) continue;
    const alvo = nearestHero(state, e.x, e.y);
    if (fam.comportamento === 'perseguir' && alvo) {
      const d = dirTo(e.x, e.y, alvo.x, alvo.y);
      e.x += d.x * fam.velocidade * DT;
      e.y += d.y * fam.velocidade * DT;
    } else if (fam.comportamento === 'senoidal' && alvo) {
      const d = dirTo(e.x, e.y, alvo.x, alvo.y);
      // deriva senoidal perpendicular (Lençolzinhos flutuam em ondas)
      const w = sinT(e.phase + state.tick / TICKS_POR_SEGUNDO / 2) * 40;
      e.x += (d.x * fam.velocidade + -d.y * w) * DT;
      e.y += (d.y * fam.velocidade + d.x * w) * DT;
    } else if (fam.comportamento === 'rolar') {
      e.x += e.vx * DT;
      e.y += e.vy * DT;
      if (e.x < 8 || e.x > config.arenaW - 8) e.vx = -e.vx;
      if (e.y < 8 || e.y > config.arenaH - 8) e.vy = -e.vy;
    }
    if (e.contactCd > 0) e.contactCd -= 1;
    if (
      alvo && e.contactCd === 0 && alvo.iframeCd === 0 &&
      dist2(e.x, e.y, alvo.x, alvo.y) <= fam.raioContato * fam.raioContato
    ) {
      alvo.hp = Math.max(0, alvo.hp - fam.dano);
      e.contactCd = CONTACT_CD_TICKS;
      alvo.iframeCd = IFRAME_TICKS; // i-frames: só 1 golpe de contato por janela, não importa a pilha
    }
  }

  // ── 4b. sufocamento DIRECIONAL: dói quando o herói está CERCADO (inimigos em muitos
  //        setores ao redor), ignorando i-frame. Um AFK é cercado 360° (todos os octantes);
  //        um kite tem inimigos só na esteira (poucos octantes) → imune. Octantes = sem atan2 (A2). ──
  for (const h of state.heroes) {
    if (!h.alive || h.down) continue;
    const ocupado = [false, false, false, false, false, false, false, false];
    for (const e of state.enemies) {
      if (!e.alive || e.boss) continue;
      const dx = e.x - h.x;
      const dy = e.y - h.y;
      if (dx * dx + dy * dy > SUFOCO_RAIO * SUFOCO_RAIO) continue;
      const ax = dx < 0 ? -dx : dx;
      const ay = dy < 0 ? -dy : dy;
      const oct = ax >= ay ? (dx >= 0 ? (dy >= 0 ? 0 : 7) : dy >= 0 ? 3 : 4) : dy >= 0 ? (dx >= 0 ? 1 : 2) : dx >= 0 ? 6 : 5;
      ocupado[oct] = true;
    }
    const setores = ocupado.reduce((n, o) => (o ? n + 1 : n), 0);
    if (setores >= SUFOCO_SETORES_MIN) {
      h.hp = Math.max(0, h.hp - (setores - (SUFOCO_SETORES_MIN - 1)) * SUFOCO_DANO * DT);
    }
  }

  // ── 4c. quedas (co-op) e reviver ────────────────────────
  for (const h of state.heroes) {
    if (!h.alive) continue;
    if (!h.down && h.hp <= 0) {
      if (h.autoRevives > 0) {
        h.autoRevives -= 1; // ACC-6: auto-revive do Modo Coragem
        h.hp = h.maxHp * REVIVE_HP_FRAC;
        h.iframeCd = IFRAME_TICKS * 3;
      } else if (config.playerCount > 1) { h.down = true; h.downTicks = 0; h.reviveTicks = 0; }
      else h.alive = false; // solo: morte imediata (preserva AC-M1-01/02)
    }
    if (h.down) {
      h.downTicks += 1;
      let aliado = false;
      for (const o of state.heroes) {
        if (o === h || !o.alive || o.down) continue;
        if (dist2(o.x, o.y, h.x, h.y) <= REVIVE_RADIO * REVIVE_RADIO) { aliado = true; break; }
      }
      if (aliado) {
        h.reviveTicks += 1;
        if (h.reviveTicks >= REVIVE_HOLD) {
          h.down = false; h.hp = h.maxHp * REVIVE_HP_FRAC; h.reviveTicks = 0; h.downTicks = 0;
          h.iframeCd = IFRAME_TICKS * 3; // curta invulnerabilidade pós-revive
        }
      } else {
        h.reviveTicks = 0; // soltou o cafuné → zera (AC-M2-03)
      }
      if (h.down && h.downTicks > DOWN_WINDOW) {
        if (h.autoRevives > 0) { // ACC-6: auto-revive salva na janela estourada
          h.autoRevives -= 1;
          h.down = false; h.hp = h.maxHp * REVIVE_HP_FRAC; h.downTicks = 0; h.reviveTicks = 0;
          h.iframeCd = IFRAME_TICKS * 3;
        } else h.alive = false; // ninguém reviveu a tempo
      }
    }
  }

  // ── 5a. armas do herói (dano contínuo por mira, doc 07 §9) ──
  for (const h of state.heroes) {
    if (!h.alive || h.down) continue;
    const antesKills = h.kills;
    applyWeapons(state, h, danoMul);
    // gemas/luar pelos abates das armas
    for (let k = 0; k < h.kills - antesKills; k++) {
      dropGemAtHero(state, rng, h, emBadalada);
    }
  }

  // ── 5b. ataque de assinatura (Pip: Cenourada 360°) ──────
  for (const h of state.heroes) {
    if (!h.alive || h.down || h.attackCd > 0) continue;
    h.attackCd = Math.round(TICKS_POR_SEGUNDO / PIP.cadencia);
    for (const e of state.enemies) {
      if (!e.alive) continue;
      if (dist2(h.x, h.y, e.x, e.y) <= PIP.raio * PIP.raio) {
        e.hp -= PIP.dano * danoMul; // Ferreiro
        const d = dirTo(h.x, h.y, e.x, e.y);
        e.x += d.x * PIP.knockback;
        e.y += d.y * PIP.knockback;
        if (e.hp <= 0) {
          e.alive = false;
          h.kills += 1;
          state.stats.kills += 1;
          dropGem(state, rng, e.x, e.y, emBadalada);
          if (rng.next01() < LUAR_CHANCE) {
            state.stats.luar += emBadalada ? 2 : 1;
            if (emBadalada) state.stats.dropsNaBadalada += 1;
          }
        }
      }
    }
  }

  // ── 5c. Golemita derrotado → vitória de verdade (só Golemita; Norno vence pelos ponteiros) ──
  if (state.phase === 'guardiao' && !state.bossDefeated && !state.norno) {
    const boss = state.enemies.find((e) => e.boss);
    if (boss && !boss.alive) {
      state.bossDefeated = true;
      state.phase = 'vitoria';
    }
  }

  // ── 5d. objetivo do capítulo (cap.2–4): mineração/escolta → vitória; NPC morto → derrota ──
  if (state.objetivo) {
    const status = atualizarObjetivo(state.objetivo, state.heroes, state.enemies, DT);
    if (status === 'completo') state.phase = 'vitoria';
    else if (status === 'falhou') state.phase = 'derrota';
  }

  // ── 5e. Norno (AC-M4-04): fases por HP; fase 3 imune → vence empurrando os ponteiros ──
  if (state.norno) {
    const boss = state.enemies.find((e) => e.boss);
    if (boss && atualizarNorno(state.norno, boss, state.heroes, config.fragmentos ?? 0) === 'vitoria') {
      state.bossDefeated = true;
      state.phase = 'vitoria';
    }
  }

  // ── 6. coleta de gemas + level-up ───────────────────────
  for (const g of state.gems) {
    if (!g.alive) continue;
    for (const h of state.heroes) {
      if (!h.alive || h.down) continue; // caídos não coletam
      if (dist2(g.x, g.y, h.x, h.y) <= GEM_PICKUP_RAIO * GEM_PICKUP_RAIO) {
        g.alive = false;
        state.stats.gemsColetadas += 1;
        const valor = g.tier === 'comum' ? 1 : g.tier === 'rara' ? 3 : 8;
        // co-op: XP de gema vai para TODOS os vivos e de pé (doc 06 D5)
        for (let hi = 0; hi < state.heroes.length; hi++) {
          const hh = state.heroes[hi]!;
          if (!hh.alive || hh.down) continue;
          hh.xp += valor;
          while (hh.level < 60 && hh.xp >= xpForLevel(hh.level + 1)) {
            hh.level += 1;
            const cartas = generateDraft(hh, rng, 3, meta.sortudo);
            if (opts?.interactive) {
              // interativo: abre o draft e CONGELA a sim até resolveDraft()
              state.pendingDraft = { heroIdx: hi, cards: cartas };
              state.rngState = rng.state();
              return state;
            }
            const escolha = autoPick(cartas); // headless/bot/co-op
            if (escolha) applyCard(hh, escolha);
          }
        }
        break;
      }
    }
  }

  // ── 6b. carga do especial: +1/abate (próprio) +0,5/gema (compartilhada) ──
  const gemasTick = state.stats.gemsColetadas - gems0;
  state.heroes.forEach((h, i) => {
    if (!h.alive || h.down) return;
    const abatesTick = h.kills - (kills0[i] ?? h.kills);
    h.special = Math.min(SPECIAL_MAX, h.special + (abatesTick * 1 + gemasTick * 0.5) * cargaMul); // Mágico
  });

  // ── 7. limpeza periódica (arrays compactos a cada 5s) ───
  if (state.tick % (5 * TICKS_POR_SEGUNDO) === 0) {
    state.enemies = state.enemies.filter((e) => e.alive);
    state.gems = state.gems.filter((g) => g.alive);
  }

  state.rngState = rng.state();
  state.tick += 1;
  return state;
}

function dropGemAtHero(state: RunState, rng: Rng, h: RunState['heroes'][number], emBadalada: boolean): void {
  // abate por arma cai perto do herói (aproximação — a posição exata do inimigo já se foi)
  dropGem(state, rng, h.x + (rng.next01() * 40 - 20), h.y + (rng.next01() * 40 - 20), emBadalada);
  if (rng.next01() < LUAR_CHANCE) {
    state.stats.luar += emBadalada ? 2 : 1;
    if (emBadalada) state.stats.dropsNaBadalada += 1;
  }
}

function dropGem(state: RunState, rng: Rng, x: number, y: number, emBadalada: boolean): void {
  const roll = rng.next01();
  const tier: GemSim['tier'] = roll < 0.02 ? 'epica' : roll < 0.16 ? 'rara' : 'comum';
  const n = emBadalada ? 2 : 1; // Badalada do Outro Lado: drops ×2 (AC-M1-05)
  for (let i = 0; i < n; i++) {
    state.gems.push({ id: state.nextId++, x: x + i * 6, y, tier, alive: true });
    if (emBadalada) state.stats.dropsNaBadalada += 1;
  }
}

function nearestHero(state: RunState, x: number, y: number) {
  let best = null as RunState['heroes'][number] | null;
  let bd = Infinity;
  for (const h of state.heroes) {
    if (!h.alive || h.down) continue; // inimigos ignoram heróis caídos
    const d = dist2(x, y, h.x, h.y);
    if (d < bd) {
      bd = d;
      best = h;
    }
  }
  return best;
}

const clamp = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const dist2 = (ax: number, ay: number, bx: number, by: number): number =>
  (ax - bx) * (ax - bx) + (ay - by) * (ay - by);

function dirTo(ax: number, ay: number, bx: number, by: number): { x: number; y: number } {
  const dx = bx - ax;
  const dy = by - ay;
  const len = Math.sqrt(dx * dx + dy * dy);
  return len < 0.0001 ? { x: 0, y: 0 } : { x: dx / len, y: dy / len };
}

/** rajada de dano em raio (especial) — conta abates para o herói/estatísticas */
function burstDamage(state: RunState, hero: RunState['heroes'][number], x: number, y: number, r: number, dano: number): void {
  const r2 = r * r;
  for (const e of state.enemies) {
    if (!e.alive || e.boss) continue;
    if ((e.x - x) * (e.x - x) + (e.y - y) * (e.y - y) <= r2) {
      e.hp -= dano;
      if (e.hp <= 0) {
        e.alive = false;
        hero.kills += 1;
        state.stats.kills += 1;
      }
    }
  }
}
